import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Skeleton,
  Paper,
  Stack,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PeopleIcon from '@mui/icons-material/People';
import MainLayout from '@/components/MainLayout';
import ServiceDialog from '@/components/ServiceDialog';
import api from '@/lib/api';
import { toast } from '@/lib/toast';

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [activeService, setActiveService] = useState(null);

  const fetchServices = async () => {
    try {
      const response = await api.get('/services');
      setServices(response.data);
    } catch {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const openCreate = () => {
    setDialogMode('create');
    setActiveService(null);
    setDialogOpen(true);
  };

  const openEdit = (service) => {
    setDialogMode('edit');
    setActiveService(service);
    setDialogOpen(true);
  };

  return (
    <MainLayout>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 4 }}>
          <Box>
            <Typography variant="h3" color="primary">
              Services
            </Typography>
            <Typography color="text.secondary" variant="body2">
              Manage service offerings
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} data-testid="add-service-button">
            Add service
          </Button>
        </Stack>

        <ServiceDialog
          open={dialogOpen}
          mode={dialogMode}
          service={activeService}
          onClose={() => setDialogOpen(false)}
          onSaved={fetchServices}
        />

        {loading ? (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" sx={{ width: { xs: '100%', md: 320 }, height: 200 }} />
            ))}
          </Stack>
        ) : services.length === 0 ? (
          <Paper variant="outlined" sx={{ py: 8, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No services created yet
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {services.map((service) => (
              <Paper
                key={service.service_id}
                variant="outlined"
                sx={{ p: 3, flex: '1 1 300px', maxWidth: { md: 400 }, '&:hover': { borderColor: 'primary.main' } }}
                data-testid={`service-card-${service.service_id}`}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    {service.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => openEdit(service)}
                    aria-label="Edit service"
                    data-testid={`edit-service-${service.service_id}`}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Stack>
                {service.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {service.description}
                  </Typography>
                )}
                <Stack spacing={1}>
                  <Stack direction="row" alignItems="center" spacing={1} color="text.secondary">
                    <AccessTimeIcon fontSize="small" />
                    <Typography variant="body2">{service.duration_minutes} minutes</Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1} color="primary.main">
                    <AttachMoneyIcon fontSize="small" />
                    <Typography fontWeight={700} variant="h6">
                      ${Number(service.price).toFixed(2)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1} color="text.secondary">
                    <PeopleIcon fontSize="small" />
                    <Typography variant="body2">Capacity: {service.capacity}</Typography>
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Box>
        )}
      </Box>
    </MainLayout>
  );
};

export default ServicesPage;
